import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["label", "as", "href", "auxiliaryText", "danger", "truncate", "selected", "disabled", "icon", "suffix", "onClick"];
import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import Text from '../Text';
import DropdownCustomItem from './DropdownCustomItem';
import DropdownLabel from './DropdownLabel';
import useDropdownContext from './useDropdownContext';
import { reasonsToToggle } from './constants';
const displayName = 'DropdownItem';
const Root = styled(DropdownCustomItem).withConfig({
  displayName: "DropdownItem__Root",
  componentId: "sc-14tlcjv-0"
})(["display:flex;text-decoration:none;&,&:hover{text-decoration:none;}", " ", " ", ""], _ref => {
  let {
    $hasIcon,
    disabled
  } = _ref;
  return $hasIcon && css(["> svg:first-child{margin-right:var(--size-spacing-02);fill:var(--neutral-icon-low-emphasis);}", ""], disabled && css(["> svg:first-child{fill:var(--neutral-icon-disabled);}"]));
}, _ref2 => {
  let {
    $danger,
    disabled
  } = _ref2;
  return $danger && !disabled && css(["", "{color:var(--danger-text);}> svg:first-child{fill:var(--danger-icon);}"], Text.Style);
}, _ref3 => {
  let {
    $selected,
    disabled
  } = _ref3;
  return $selected && !disabled && css(["", "{color:var(--primary-text);}> svg:first-child{fill:var(--primary-icon);}"], Text.Style);
});
const DropdownItem = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
      label,
      as: asProp,
      href,
      auxiliaryText,
      danger,
      truncate,
      selected,
      disabled,
      icon,
      suffix,
      onClick
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded);
  const as = href ? 'a' : asProp;
  const {
    toggle
  } = useDropdownContext();
  const handleClick = useCallback(event => {
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
    toggle(event, reasonsToToggle.clickItem);
  }, [onClick, toggle]);
  return /*#__PURE__*/React.createElement(Root, _extends({}, rest, {
    href: href,
    forwardedAs: as,
    disabled: disabled,
    "aria-current": selected,
    $hasIcon: !!icon,
    $danger: danger,
    $truncate: truncate,
    $selected: selected,
    onClick: handleClick,
    ref: ref
  }), icon, /*#__PURE__*/React.createElement(DropdownLabel, {
    label: label,
    auxiliaryText: auxiliaryText,
    truncate: truncate,
    suffix: suffix
  }));
});
DropdownItem.displayName = displayName;
DropdownItem.Style = Root;
DropdownItem.defaultProps = {
  as: 'div',
  disabled: false,
  danger: false,
  truncate: false,
  selected: false
};
process.env.NODE_ENV !== "production" ? DropdownItem.propTypes = {
  /**
   * Set element type to be rendered for this component.
   * By default it will be render a `<div>` element.
   *
   * If the component has the `href` prop, it will be render as a link `<a>`
   * regardless of the value of `as` prop.
   */
  as: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  /**
   * Disables the `Dropdown.Item`.
   */
  disabled: PropTypes.bool,
  /**
   * Sets the label, the main text displayed by the component.
   */
  label: PropTypes.string.isRequired,
  /**
   * Sets the auxiliary text.
   */
  auxiliaryText: PropTypes.string,
  /**
   * Indicates if the item is visually dangerous.
   */
  danger: PropTypes.bool,
  /**
   * Identifies that the label and the auxiliary text will
   * be truncated when it exceeds the available size.
   */
  truncate: PropTypes.bool,
  /**
   * Indicates if the item is selected.
   */
  selected: PropTypes.bool,
  /**
   * Sets icon to be displayed at the beginning of the item.
   */
  icon: PropTypes.node,
  /**
   * Sets a content to be displayed at the end of the item.
   */
  suffix: PropTypes.node
} : void 0;
export default DropdownItem;